import React, { useMemo, useRef } from "react"
import Text from "@/components/common/Text"
import useIsDebug from "@/hooks/useIsDebug"
import useInView from "@/hooks/useInView"
import { duration, gsap, useGSAP } from "@/utils/gsap"
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect"

const DEFAULT_PROPS = {
	id: "",
	text: "",
	chars: "",
	onInView: false,
	animationVars: {},
	timeline: null,
	pinnedContainer: { current: null },
	axis: "y",
	once: false,
	paused: false,
	debug: false,
	disabled: false,
}

const TEXT_SHUFFLE_CLASSNAME = "text-shuffle-"

const TRIGGERS = {
	DEFAULT: {
		start: `top bottom`,
		end: `bottom top`,
	},
	ONCE: {
		start: `top bottom`,
		end: `top bottom`,
	},
}

const AnimateTextShuffle = ({ target, timeline = null, text = "", chars = "1234" }) => {
	const tl = timeline || gsap.timeline()
	tl.to(target, {
		scrambleText: { text, chars },
		duration: duration * 2,
		ease: "sine.inOut",
	})
	return tl
}

export function useTextShuffle({
	el,
	onInView = DEFAULT_PROPS.onInView,
	text = DEFAULT_PROPS.text,
	chars = DEFAULT_PROPS.chars,
	id: passedId = DEFAULT_PROPS.id,
	animationVars = DEFAULT_PROPS.animationVars,
	debug: forceDebug = DEFAULT_PROPS.debug,
	once = DEFAULT_PROPS.once,
	timeline = DEFAULT_PROPS.timeline,
	disabled = DEFAULT_PROPS.disabled,
	paused = DEFAULT_PROPS.paused,
	pinnedContainer = DEFAULT_PROPS.pinnedContainer,
}) {
	const debug = useIsDebug(forceDebug)
	const memoPinnedContainer = useRef(null)
	memoPinnedContainer.current = pinnedContainer?.current
	const props = useMemo(() => {
		const trigger = once ? TRIGGERS.ONCE : TRIGGERS.DEFAULT
		return {
			id: `${TEXT_SHUFFLE_CLASSNAME}-${passedId}`,
			disabled: !onInView || disabled,
			trigger,
			markers: debug,
			once: debug ? !debug : once,
			pinnedContainer: memoPinnedContainer,
		}
	}, [debug, onInView, disabled, passedId, once])

	const inViewProps = useInView(el, props)
	const trigger = useMemo(() => {
		const trigger = once ? "intersected" : "inView"
		return inViewProps[trigger]
	}, [inViewProps, once])
	const animationTL = useMemo(() => timeline || gsap.timeline(), [timeline])
	const memoAnimationVars = useRef(animationVars)

	useIsomorphicLayoutEffect(() => {
		memoAnimationVars.current = animationVars
	}, [animationVars])

	useGSAP(
		() => {
			// console.log(trigger);
			if (!disabled && el?.current && trigger) {
				animationTL.pause()
				const vars = memoAnimationVars.current
				AnimateTextShuffle({
					target: el.current,
					text,
					chars,
					vars,
					timeline: animationTL,
				})

				if (!paused) {
					// console.log('play');
					animationTL.revert()
					animationTL.play(0)
				}
			}
		},
		{
			dependencies: [trigger],
			revertOnUpdate: true,
		},
	)
}

const TextShuffle = ({
	id = DEFAULT_PROPS.id,
	text = DEFAULT_PROPS.text,
	chars = DEFAULT_PROPS.chars,
	onInView = DEFAULT_PROPS.onInView,
	animationVars = DEFAULT_PROPS.animationVars,
	timeline = DEFAULT_PROPS.timeline,
	once = DEFAULT_PROPS.once,
	debug = DEFAULT_PROPS.debug,
	...props
}) => {
	const ElRef = useRef()

	useTextShuffle({
		el: ElRef,
		text,
		chars,
		id,
		onInView,
		animationVars,
		debug,
		once,
		timeline,
		...props,
	})

	return <Text ref={ElRef}>{text}</Text>
}

export default TextShuffle
